import {useState} from 'react';

import {isNil} from 'ramda';

import type {Nullish} from 'shared/web';

import type {Preset} from '../../../types/Api';

/**
 * In-memory, non-persisted color overrides for the live "preview" UX: while the
 * edit dialog is open, every color the user hovers is reflected on the preset tab
 * behind it. The persisted value lives on `preset.color` (written to the BE via
 * create/rename); this hook only layers a transient preview on top until the save
 * refreshes the list. On cancel, EditPresetDialog reverts by previewing the
 * original color again.
 */
export function usePresetColorPreview(presets: Preset[] | undefined) {
  const [previews, setPreviews] = useState<Map<string, string>>(new Map());
  const [previousPresets, setPreviousPresets] = useState(presets);

  if (previousPresets !== presets) {
    setPreviousPresets(presets);
    setPreviews(new Map());
  }

  const resolveSavedColor = (presetId: string, savedColor?: string | Nullish) => {
    const preset = presets?.find((item) => item.id === presetId);
    const color = savedColor === undefined ? preset?.color : savedColor;
    return color ?? '';
  };

  const setPreviewColor = (presetId: string, color?: string) => {
    setPreviews((prev) => {
      const next = new Map(prev);
      if (isNil(color) || color === resolveSavedColor(presetId)) {
        next.delete(presetId);
      } else {
        next.set(presetId, color);
      }
      return next;
    });
  };

  const resolveColor = (presetId: string, savedColor?: string | Nullish) => {
    const color = previews.get(presetId) ?? resolveSavedColor(presetId, savedColor);
    return /^#[0-9a-fA-F]{6}$/.test(color) ? color : '';
  };

  return {setPreviewColor, resolveColor} as const;
}
